import { useCallback, useEffect, useState, useTransition } from 'react'
import type { AuthUser } from '../context/authContextCore'
import type { WorkspaceRole } from '../context/WorkspaceContext'
import { roleCanPerform } from '../utils/workspacePermissions'

interface AppLifecycleProps {
  activeWorkspaceId: string | null
  activeRole: WorkspaceRole | null
  currentUser: AuthUser | null
  inviteToken: string | null
  onClearInvite: () => void
}

export type WorkspaceSettingsIntent = 'TRANSFER' | 'SUMMARY' | 'REPORT'

export function useAppLifecycle({
  activeWorkspaceId,
  activeRole,
  currentUser,
  inviteToken,
  onClearInvite,
}: AppLifecycleProps) {
  const [rawAdminPanelOpen, setRawAdminPanelOpen] = useState(false)
  const [rawMemberPanelOpen, setRawMemberPanelOpen] = useState(false)
  const [workspaceSettingsIntent, setWorkspaceSettingsIntent] = useState<WorkspaceSettingsIntent | null>(null)
  const [, startTransition] = useTransition()

  useEffect(() => {
    if (!currentUser || !inviteToken) return

    startTransition(() => {
      onClearInvite()
    })
  }, [currentUser, inviteToken, onClearInvite])

  const closeWorkspacePanels = useCallback(() => {
    setRawAdminPanelOpen(false)
    setRawMemberPanelOpen(false)
    setWorkspaceSettingsIntent(null)
  }, [])

  const openWorkspaceSettings = useCallback(
    (intentOrRole?: WorkspaceSettingsIntent | WorkspaceRole | null, roleOverride?: WorkspaceRole | null) => {
      const isIntent = intentOrRole === 'TRANSFER' || intentOrRole === 'SUMMARY' || intentOrRole === 'REPORT'
      const intent = isIntent ? intentOrRole : null
      const role = (isIntent ? roleOverride : (intentOrRole as WorkspaceRole | null | undefined)) ?? activeRole
      setRawAdminPanelOpen(Boolean(role && roleCanPerform(role, 'ADMIN')))
      setRawMemberPanelOpen(!role || !roleCanPerform(role, 'ADMIN'))
      setWorkspaceSettingsIntent(intent)
    },
    [activeRole],
  )

  const consumeWorkspaceSettingsIntent = useCallback(() => {
    setWorkspaceSettingsIntent(null)
  }, [])

  const isAdminPanelOpen = Boolean(activeWorkspaceId && activeRole && roleCanPerform(activeRole, 'ADMIN') && rawAdminPanelOpen)
  const isMemberPanelOpen = Boolean(activeWorkspaceId && (!activeRole || !roleCanPerform(activeRole, 'ADMIN')) && rawMemberPanelOpen)

  return {
    isAdminPanelOpen,
    isMemberPanelOpen,
    setIsAdminPanelOpen: setRawAdminPanelOpen,
    setIsMemberPanelOpen: setRawMemberPanelOpen,
    openWorkspaceSettings,
    closeWorkspacePanels,
    workspaceSettingsIntent,
    consumeWorkspaceSettingsIntent,
  }
}
